import React from 'react';
import { Button, Table } from '@parsina/framework';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { Code, CountCell, MarginBadge, useEnumLabel, useServiceLabel } from './shared';
import {
  EarnedCell, PaidCell, RateCell, ShareCell, useEarnedUnit, usePaidUnit,
} from './VisitCells';

/**
 * «به تفکیک قابلیت» — what one visit earns and costs, per feature (PM-0427).
 *
 * The rows are the service's GROUPS («پایش بالینی», «تشخیص افتراقی», «تکمیل
 * یادداشت SOAP», the readers, «بررسی نسخه», the rest), in the order the API
 * sends them; a group opens onto its services. Every figure is a per-visit
 * AVERAGE over all the filtered visits — a visit that never used the feature
 * contributed a real 0 — so the groups add up to the closing «هر ویزیت» row,
 * which is the stats strip's own figures.
 *
 * The expansion is local state: which group is open is not a filter, and a
 * link to this view should not carry it.
 */
export default function VisitFeaturesTable({ groups, stats, conv }) {
  const { t } = useTranslation();
  const groupLabel = useEnumLabel('aiPricing.visits.groups');
  const serviceLabel = useServiceLabel();
  const earnedUnit = useEarnedUnit(conv);
  const paidUnit = usePaidUnit(conv);
  const [open, setOpen] = React.useState(() => new Set());

  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const rows = [];
  for (const g of groups || []) {
    const key = g.total?.key || g.total?.group;
    rows.push({ kind: 'group', id: `group-${key}`, key, row: g.total, count: (g.services || []).length });
    if (open.has(key)) {
      for (const s of g.services || []) rows.push({ kind: 'service', id: `service-${s.key}`, key: s.key, row: s });
    }
  }
  if (stats && rows.length) {
    rows.push({
      kind: 'total',
      id: 'total',
      key: 'total',
      row: {
        visitsUsing: stats.visits,
        shareOfVisits: null,
        runsPerVisit: stats.runsPerVisit,
        chargedRunsPerVisit: stats.chargedRunsPerVisit,
        earnedParsehPerVisit: stats.meanEarnedParseh,
        paidUsdPerVisit: stats.meanPaidUsd,
        marginPct: stats.marginPct,
        unpricedRuns: stats.unpricedRuns,
      },
    });
  }

  const columns = [
    {
      key: 'feature',
      header: t('aiPricing.visits.features.columns.feature'),
      render: (r) => {
        if (r.kind === 'total') return <span className="font-bold text-foreground">{t('aiPricing.visits.features.total')}</span>;
        if (r.kind === 'service') {
          const label = serviceLabel({ ...r.row, serviceKey: r.row.key });
          return (
            <div className="min-w-0 ps-7">
              <div className="truncate text-foreground" title={label}>{label}</div>
              <div className="truncate text-xs text-muted-foreground"><Code>{r.row.key}</Code></div>
            </div>
          );
        }
        const expanded = open.has(r.key);
        return (
          <span className="inline-flex items-center gap-1">
            {r.count ? (
              <Button
                variant="ghost"
                size="sm"
                aria-expanded={expanded}
                aria-label={t(expanded ? 'aiPricing.visits.features.collapse' : 'aiPricing.visits.features.expand', { group: groupLabel(r.key) })}
                onClick={() => toggle(r.key)}
                data-testid={`ai-visits-features-toggle-${r.key}`}
              >
                {expanded ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
              </Button>
            ) : null}
            <span className="font-semibold text-foreground">{groupLabel(r.key)}</span>
          </span>
        );
      },
    },
    {
      key: 'share',
      header: t('aiPricing.visits.features.columns.share'),
      align: 'end',
      width: '8rem',
      render: (r) => (r.kind === 'total' ? (
        <CountCell value={r.row.visitsUsing} />
      ) : (
        <span className="inline-flex flex-col items-end gap-0.5">
          <ShareCell value={r.row.shareOfVisits} />
          <span className="text-[11px] text-muted-foreground">
            {t('aiPricing.visits.features.visitsUsing', { count: r.row.visitsUsing ?? 0 })}
          </span>
        </span>
      )),
    },
    { key: 'runs', header: t('aiPricing.visits.features.columns.runs'), align: 'end', width: '7rem', render: (r) => <RateCell value={r.row.runsPerVisit} /> },
    { key: 'charged', header: t('aiPricing.visits.features.columns.charged'), align: 'end', width: '7rem', render: (r) => <RateCell value={r.row.chargedRunsPerVisit} /> },
    {
      key: 'earned',
      header: t('aiPricing.visits.features.columns.earned', { unit: earnedUnit }),
      align: 'end',
      width: '9rem',
      render: (r) => <EarnedCell parseh={r.row.earnedParsehPerVisit} conv={conv} data-testid={`ai-visits-features-${r.id}-earned`} />,
    },
    {
      key: 'paid',
      header: t('aiPricing.visits.features.columns.paid', { unit: paidUnit }),
      align: 'end',
      width: '9rem',
      render: (r) => <PaidCell usd={r.row.paidUsdPerVisit} conv={conv} partial={(r.row.unpricedRuns || 0) > 0} data-testid={`ai-visits-features-${r.id}-paid`} />,
    },
    { key: 'margin', header: t('aiPricing.visits.features.columns.margin'), align: 'end', width: '6rem', render: (r) => <MarginBadge value={r.row.marginPct} data-testid={`ai-visits-features-${r.id}-margin`} /> },
  ];

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.visits.features.aria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      rowProps={(r) => ({
        'data-testid': `ai-visits-features-row-${r.id}`,
        'data-kind': r.kind,
        className: r.kind === 'total' ? 'bg-muted' : undefined,
      })}
      data-testid="ai-visits-features-table"
    />
  );
}
